import type { PixChargeStatus, PixPaymentStatus, PixProviderId, PixWebhookHealth } from "@cx/contracts";
import type { PixKeyKind } from "@cx/domain";
import type { Tone } from "@cx/ui";

export const PROVIDER_LABEL: Record<PixProviderId, string> = {
  sandbox: "Sandbox (simulado, para testes)",
  asaas: "Asaas",
  efi: "Efí (ex-Gerencianet)",
};

/** Provedor real padrão (decisão da Fabi, 03/10/2026). */
export const DEFAULT_PROVIDER: PixProviderId = "asaas";

/** Opções do seletor: Asaas primeiro, Efí, e o sandbox só fora de produção (ou se já for o configurado). */
export function providerOptions(sandboxAllowed: boolean, current: PixProviderId): PixProviderId[] {
  return sandboxAllowed || current === "sandbox" ? ["asaas", "efi", "sandbox"] : ["asaas", "efi"];
}

/** Como a tela mostra o endereço do webhook: o segredo nunca aparece. */
export function webhookHint(provider: PixProviderId, webhookPath: string): { path: string; note: string } {
  if (provider === "asaas")
    return {
      path: webhookPath,
      note: "autenticado pelo token que o Asaas envia no cabeçalho asaas-access-token (cadastrado direto no Asaas e não exibido).",
    };
  return {
    path: `${webhookPath}/…`,
    note: "o final do endereço é um segredo cadastrado direto no provedor e não é exibido.",
  };
}

export const KEY_KIND_LABEL: Record<PixKeyKind, string> = {
  cpf: "CPF",
  cnpj: "CNPJ",
  email: "E-mail",
  phone: "Celular",
  evp: "Chave aleatória",
};

export const CHARGE_BADGE: Record<PixChargeStatus, { label: string; tone: Tone }> = {
  creating: { label: "Gerando", tone: "secondary" },
  active: { label: "Aguardando pagamento", tone: "info" },
  expired: { label: "Vencido", tone: "warning" },
  paid: { label: "Pago", tone: "success" },
  canceled: { label: "Cancelado", tone: "secondary" },
  failed: { label: "Falhou", tone: "danger" },
};

export const PAYMENT_STATUS_LABEL: Record<PixPaymentStatus, string> = {
  received: "Recebido, conferindo",
  applied: "Baixado",
  review: "Em revisão",
  rejected: "Não confirmado",
};

const REVIEW_LABEL: Record<string, string> = {
  underpaid: "pago a menor (baixa parcial)",
  overpaid: "pago a maior (excedente não baixado)",
  title_closed: "a conta já estava quitada ou cancelada",
  unknown_txid: "cobrança não encontrada",
  not_confirmed: "o provedor não confirmou o pagamento",
  "not_confirmed:not_found": "o provedor não encontrou a cobrança; use Consultar no provedor",
  "not_confirmed:not_listed": "o provedor ainda não listou este Pix; use Consultar no provedor",
  "not_confirmed:mismatch": "o provedor informou valor ou cobrança diferente",
  "not_confirmed:unknown_payment": "o provedor não conhece este Pix",
  provider_changed: "o provedor do Pix mudou na configuração",
  dlq: "o provedor ficou fora do ar; use Consultar no provedor",
  refunded: "o Pix foi estornado no provedor depois do pagamento (confira a baixa)",
  partially_refunded: "o Pix foi estornado em parte no provedor (confira a baixa)",
  chargeback: "o pagador contestou o Pix no provedor (confira a baixa)",
};

/**
 * Aviso da tela de configuração sobre o webhook do provedor (Asaas, revisão de segurança B1): fila interrompida ou
 * desativada, webhook removido no provedor, ou notificação recente fora do formato. null = nada a avisar.
 */
export function webhookHealthWarning(
  provider: PixProviderId,
  h: PixWebhookHealth | null | undefined,
  now = Date.now(),
): string | null {
  if (provider !== "asaas" || !h) return null;
  if (h.registered === false)
    return "O webhook do Pix não está cadastrado no Asaas. Salve com “Gerar novo segredo do webhook” para cadastrar de novo; até lá, use Consultar no provedor nas cobranças.";
  if (h.interrupted)
    return "A fila de notificações do Asaas está interrompida: os pagamentos não chegam sozinhos. Reative a fila em Integrações › Webhooks no Asaas e use Consultar no provedor nas cobranças pendentes.";
  if (h.enabled === false)
    return "O webhook do Pix está desativado no Asaas. Ative-o em Integrações › Webhooks no Asaas.";
  if (h.lastInvalidAt && now - Date.parse(h.lastInvalidAt) < 7 * 86_400_000)
    return "O Asaas enviou notificações fora do formato esperado nos últimos dias (foram ignoradas). Se algum Pix pago não baixou, use Consultar no provedor.";
  return null;
}

/** Motivo da revisão em português; `settlement_failed:<código>` vira "a baixa foi recusada (<código>)". */
export function reviewLabel(reason: string | null): string | null {
  if (!reason) return null;
  if (reason.startsWith("settlement_failed:")) return `a baixa foi recusada (${reason.slice(18)})`;
  return REVIEW_LABEL[reason] ?? reason;
}

/** Validade em horas para o formulário (a API trabalha em segundos). */
export const EXPIRATION_OPTIONS: { seconds: number; label: string }[] = [
  { seconds: 1800, label: "30 minutos" },
  { seconds: 3600, label: "1 hora" },
  { seconds: 6 * 3600, label: "6 horas" },
  { seconds: 24 * 3600, label: "24 horas" },
  { seconds: 3 * 86400, label: "3 dias" },
  { seconds: 7 * 86400, label: "7 dias" },
  { seconds: 30 * 86400, label: "30 dias" },
];
